import React from 'react';
import { motion } from 'framer-motion';
import { BarChart3, PiggyBank, CreditCard, LineChart, Video, ArrowRight } from 'lucide-react';
import { Button } from './Button';
const cards = [{
  title: 'Presupuesto Mensual',
  description: 'Registra tus ingresos y gastos de forma simple para saber cuánto dinero tienes disponible y planificar mejor cada mes.',
  icon: BarChart3,
  image: 'https://cdn.magicpatterns.com/uploads/frC26ws6ytZbUju9m2NUof/presupuesto_mensual.png',
  delay: 0
}, {
  title: 'Plan de Ahorro',
  description: 'Define tus objetivos de ahorro y visualiza tu progreso mes a mes.',
  icon: PiggyBank,
  image: 'https://cdn.magicpatterns.com/uploads/kFpaeSwzAPsXK7gVg5ASJ5/ahorros.png',
  delay: 0.1
}, {
  title: 'Gestión de Deudas',
  description: 'Organiza tus deudas, cuotas y pagos para visualizar tu progreso y tener un plan claro.',
  icon: CreditCard,
  image: 'https://cdn.magicpatterns.com/uploads/qUAqVBEcFDywYa2VmfcsVt/deudas.png',
  delay: 0.2
}, {
  title: 'Resumen Anual',
  description: 'Obtén una visión completa de tu evolución financiera a lo largo del año.',
  icon: LineChart,
  image: 'https://cdn.magicpatterns.com/uploads/bqVA5JpGe1VeATUYHUG8Mi/resumen_anual.png',
  delay: 0.3
}, {
  title: 'Video Tutorial Paso a Paso incluido',
  description: 'Aprende desde cero cómo usar Orden Financiero™, aunque nunca hayas abierto Google Sheets.',
  icon: Video,
  image: 'https://cdn.magicpatterns.com/uploads/h8YKVztxDQvFmF99aMJaNm/Instrucciones.png',
  delay: 0.4
}];
export function WhatIncludes() {
  return <section className="py-10 md:py-20 bg-white" data-id="element-206" data-mp="18qzjmvpkss1t">
      <div className="container mx-auto px-4 md:px-6" data-id="element-207" data-mp="18sajwljtbho6">
        <div className="text-center max-w-3xl mx-auto mb-8 md:mb-16" data-id="element-208" data-mp="3fh5p6clqewgm">
          <h2 className="font-display text-2xl md:text-5xl font-extrabold text-gray-900 tracking-tight mb-2 md:mb-4" data-id="element-209" data-mp="3aiug4439gpzg">
            Todo lo que necesitas para tomar el control de tus finanzas
          </h2>
          <p className="text-sm md:text-xl text-gray-600" data-id="element-210" data-mp="1jej3ck76c6r6">
            Un sistema simple para organizar tu dinero, entender tu situación
            actual y comenzar a avanzar con objetivos claros.
          </p>
        </div>

        {/* Grid Layout */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-8 mb-8 md:mb-16" data-id="element-211" data-mp="1sh23uxnhm7a9">
          {/* First 3 cards */}
          {cards.slice(0, 3).map((card, index) => <Card key={index} {...card} data-id="element-212" data-mp="22cdilfywbei0" data-mpi={index} />)}

          {/* Last 2 cards centered in bottom row on desktop */}
          <div className="lg:col-span-3 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4 md:gap-8 lg:w-2/3 lg:mx-auto" data-id="element-213" data-mp="2756my2qgcktk">
            {cards.slice(3, 5).map((card, index) => <Card key={index + 3} {...card} data-id="element-214" data-mp="2g6edtzqn2jdm" data-mpi={index} />)}
          </div>
        </div>

        <div className="text-center" data-id="element-215" data-mp="2l4xyzxxerr4x">
          <Button href="#precio" className="px-6 md:px-10 py-4 text-sm md:text-lg group" data-id="element-216" data-mp="2l1tn0edpbr3p">
            SÍ, QUIERO ORDENAR MIS FINANZAS HOY
            <ArrowRight className="ml-2 w-4 h-4 md:w-5 md:h-5 group-hover:translate-x-1 transition-transform" data-id="element-217" data-mp="2q7fapf7bq7aq" />
          </Button>
        </div>
      </div>
    </section>;
}
function Card({
  title,
  description,
  icon: Icon,
  image,
  delay
}: any) {
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true
  }} transition={{
    delay,
    duration: 0.5
  }} whileHover={{
    y: -4
  }} className="bg-white rounded-2xl overflow-hidden shadow-lg border border-gray-100 hover:shadow-xl transition-shadow flex flex-col h-full" data-id="element-218" data-mp="3dz47b7r6hhxh">
      <div className="h-32 md:h-48 overflow-hidden bg-gray-50" data-id="element-219" data-mp="kzf0cmzrlvk">
        <img src={image} alt={title} className="w-full h-full object-cover transition-transform duration-500 hover:scale-105" data-id="element-220" data-mp="3ry3yam25y8wt" />
      </div>
      <div className="p-4 md:p-6 flex-1 flex flex-col" data-id="element-221" data-mp="3hf5rya862f7u">
        <div className="flex items-center gap-2 md:gap-3 mb-2 md:mb-4" data-id="element-222" data-mp="3hgqm3yqkjw56">
          <div className="p-1.5 md:p-2 bg-teal-50 rounded-lg text-teal-600" data-id="element-223" data-mp="33gpgy739u2jw">
            <Icon className="w-4 h-4 md:w-5 md:h-5" data-id="element-224" data-mp="37gpxpyduepu4" />
          </div>
          <h3 className="font-display text-base md:text-xl font-bold text-gray-900 tracking-tight" data-id="element-225" data-mp="37j2n2wlxk2h7">
            {title}
          </h3>
        </div>
        <p className="text-gray-600 leading-relaxed text-xs md:text-sm flex-1" data-id="element-226" data-mp="3zhilmai2433">
          {description}
        </p>
      </div>
    </motion.div>;
}